import { useState } from 'react'
import type { AGFunnelHook } from './useArbeitgeberFunnel'
import type { ArbeitgeberFunnelData } from '../../lib/types'
import {
  QUALIFIKATIONEN,
  ARBEITSBEREICHE,
  ARBEITSMODELLE,
  EINRICHTUNGSTYPEN,
  BUNDESLAENDER,
  SPRACHLEVEL,
  HAEUFIGKEIT_AG,
  EINSPRINGEN_HAEUFIGKEIT,
  WOCHENEND_FREQUENZ,
  FEEDBACK_RHYTHMUS,
  BENEFITS_OPTIONEN,
  ZUSCHLAEGE_STUFEN,
} from '../../lib/constants'
import { CATEGORY_BY_ID, agSlidersForCategory, type CategoryId } from '../../lib/matching/catalog'
import { supabase } from '../../lib/supabase'

interface Props {
  funnel: AGFunnelHook
  questionId: string
}

export default function ArbeitgeberQuestions({ funnel, questionId }: Props) {
  const { data, updateField } = funnel

  if (questionId.startsWith('cat_')) {
    return <QCategory funnel={funnel} categoryId={questionId.slice(4) as CategoryId} />
  }

  switch (questionId) {
    case 'einrichtung_basis':
      return <QEinrichtungBasis data={data} updateField={updateField} />
    case 'standort':
      return <QStandort data={data} updateField={updateField} />
    case 'kontakt':
      return <QKontakt data={data} updateField={updateField} />
    case 'bedarf_qualifikationen':
      return <QBedarfQualifikationen data={data} updateField={updateField} />
    case 'bedarf_anforderung':
      return <QBedarfAnforderung data={data} updateField={updateField} />
    case 'bedarf_arbeitszeit':
      return <QBedarfArbeitszeit data={data} updateField={updateField} />
    case 'verguetung':
      return <QVerguetung data={data} updateField={updateField} />
    case 'schicht_anforderung':
      return <QSchichtAnforderung data={data} updateField={updateField} />
    case 'dienstplan_fakten':
      return <QDienstplanFakten data={data} updateField={updateField} />
    case 'fakten':
      return <QFakten data={data} updateField={updateField} />
    case 'onboarding':
      return <QOnboarding data={data} updateField={updateField} />
    case 'consent':
      return <QConsent funnel={funnel} />
    default:
      return null
  }
}

type UF = <K extends keyof ArbeitgeberFunnelData>(field: K, value: ArbeitgeberFunnelData[K]) => void
type QProps = { data: ArbeitgeberFunnelData; updateField: UF }

/* ---- Bausteine ---- */

function BipolarSlider({
  value,
  onChange,
  minLabel,
  maxLabel,
}: {
  value: number
  onChange: (v: number) => void
  minLabel: string
  maxLabel: string
}) {
  return (
    <div className="bp-slider">
      <input
        type="range"
        className="funnel-slider"
        min={-5}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
      />
      <div className="bp-slider-ends">
        <span>{minLabel}</span>
        <span className="bp-slider-mid">neutral</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  )
}

function NumberField({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string
  hint?: string
  value: number | null
  onChange: (v: number | null) => void
  placeholder?: string
}) {
  return (
    <div className="funnel-field">
      <label className="funnel-label">
        {label}
        {hint && <span className="funnel-label-hint"> — {hint}</span>}
      </label>
      <input
        className="funnel-input"
        type="number"
        inputMode="numeric"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value))}
      />
    </div>
  )
}

function HaeufigkeitToggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="toggle-group">
      {HAEUFIGKEIT_AG.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`toggle-btn ${value === o.value ? 'toggle-btn--selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function YesNo({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) {
  return (
    <div className="toggle-group">
      <button type="button" className={`toggle-btn ${value === true ? 'toggle-btn--selected' : ''}`} onClick={() => onChange(true)}>✅ Ja</button>
      <button type="button" className={`toggle-btn ${value === false ? 'toggle-btn--selected' : ''}`} onClick={() => onChange(false)}>❌ Nein</button>
    </div>
  )
}

function InfoExpand({ summary, details }: { summary: string; details: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="fact-hint">
      <div className="fact-hint-row">
        <span>{summary}</span>
        <button
          type="button"
          className="fact-info-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Hinweis schließen' : 'Mehr Information'}
          title={open ? 'Hinweis schließen' : 'Mehr Information'}
        >
          {open ? '✕' : 'ℹ︎'}
        </button>
      </div>
      {open && <p className="fact-info-text">{details}</p>}
    </div>
  )
}

const TRANSPARENZ_INFO =
  'Diese Angaben gehen NICHT an Bewerber. Sie dienen ausschließlich unserem internen Matching. ' +
  'Bitte seien Sie ruhig ehrlich — auch wenn z. B. der Dienstplan nicht oft eingehalten werden kann ' +
  'oder Überstunden hoch sind, finden wir geeignete Kandidaten. Je realistischer Ihre Einschätzung, ' +
  'desto besser passt der Match-Vorschlag zur Realität in Ihrer Einrichtung.'

/* ============================================
   PHASE 1 — EINRICHTUNG
   ============================================ */

function QEinrichtungBasis({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="ag-name">Name der Einrichtung *</label>
        <input id="ag-name" className="funnel-input" type="text" placeholder="z.B. Seniorenzentrum Sonnenhof" value={data.unternehmensname} onChange={(e) => updateField('unternehmensname', e.target.value)} autoFocus />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Einrichtungstyp</label>
        <select className="funnel-input" value={data.einrichtungstyp} onChange={(e) => updateField('einrichtungstyp', e.target.value)}>
          <option value="">Auswählen...</option>
          {EINRICHTUNGSTYPEN.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
    </>
  )
}

function QStandort({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-input-row">
        <div className="funnel-field">
          <label className="funnel-label">PLZ</label>
          <input className="funnel-input" type="text" inputMode="numeric" placeholder="z.B. 80331" value={data.plz} onChange={(e) => updateField('plz', e.target.value)} />
        </div>
        <div className="funnel-field">
          <label className="funnel-label">Ort</label>
          <input className="funnel-input" type="text" placeholder="Ort" value={data.ort} onChange={(e) => updateField('ort', e.target.value)} />
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Bundesland *</label>
        <select className="funnel-input" value={data.bundesland} onChange={(e) => updateField('bundesland', e.target.value)}>
          <option value="">Auswählen...</option>
          {BUNDESLAENDER.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </div>
    </>
  )
}

function QKontakt({ data, updateField }: QProps) {
  return (
    <>
      <p className="question-hint">An wen sollen wir uns mit passenden Profilen wenden?</p>
      <div className="funnel-field">
        <label className="funnel-label">Name *</label>
        <input className="funnel-input" type="text" placeholder="Vor- und Nachname" value={data.ansprechpartner_name} onChange={(e) => updateField('ansprechpartner_name', e.target.value)} autoFocus />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Position</label>
        <input className="funnel-input" type="text" placeholder="z.B. Pflegedienstleitung" value={data.ansprechpartner_position} onChange={(e) => updateField('ansprechpartner_position', e.target.value)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">E-Mail *</label>
        <input className="funnel-input" type="email" placeholder="kontakt@einrichtung.de" value={data.ansprechpartner_email} onChange={(e) => updateField('ansprechpartner_email', e.target.value)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Telefon</label>
        <input className="funnel-input" type="tel" placeholder="Telefonnummer" value={data.ansprechpartner_telefon} onChange={(e) => updateField('ansprechpartner_telefon', e.target.value)} />
      </div>
    </>
  )
}

/* ============================================
   PHASE 2 — BEDARF
   ============================================ */

function QBedarfQualifikationen({ data, updateField }: QProps) {
  const toggleQ = (v: string) => {
    const cur = data.gewuenschte_qualifikationen || []
    updateField('gewuenschte_qualifikationen', cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])
  }
  const toggleB = (v: string) => {
    const cur = data.einsatzbereiche || []
    updateField('einsatzbereiche', cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])
  }
  return (
    <>
      <p className="question-hint">Mehrfachauswahl — alle Qualifikationen, mit denen Sie arbeiten würden.</p>
      <div className="funnel-field">
        <label className="funnel-label">Gesuchte Qualifikationen</label>
        <div className="select-cards">
          {QUALIFIKATIONEN.filter((q) => q.value !== 'sonstiges').map((qu) => (
            <div key={qu.value} className={`select-card ${(data.gewuenschte_qualifikationen || []).includes(qu.value) ? 'select-card--selected' : ''}`} onClick={() => toggleQ(qu.value)}>
              <span className="select-card-icon">{qu.icon}</span>
              <span className="select-card-label">{qu.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Einsatzbereiche mit Bedarf <span className="funnel-label-hint">— Mehrfachauswahl</span></label>
        <div className="select-cards">
          {ARBEITSBEREICHE.filter((b) => b.value !== 'sonstiges').map((b) => (
            <div key={b.value} className={`select-card ${(data.einsatzbereiche || []).includes(b.value) ? 'select-card--selected' : ''}`} onClick={() => toggleB(b.value)}>
              <span className="select-card-icon">{b.icon}</span>
              <span className="select-card-label">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function QBedarfAnforderung({ data, updateField }: QProps) {
  return (
    <>
      <NumberField label="Mindestberufserfahrung" hint="Jahre (0 = keine)" value={data.mindestberufserfahrung} onChange={(v) => updateField('mindestberufserfahrung', v)} placeholder="z.B. 2" />
      <div className="funnel-field">
        <label className="funnel-label">
          Ist eine abgeschlossene Anerkennung erforderlich?
          <span className="funnel-label-hint"> — nur relevant bei ausländischen Fachkräften</span>
        </label>
        <div className="toggle-group">
          {[['ja', 'Ja, zwingend'], ['optional', 'Optional'], ['nein', 'Nein']].map(([v, l]) => (
            <button key={v} type="button" className={`toggle-btn ${data.anerkennung_erforderlich === v ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('anerkennung_erforderlich', v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">
          Erforderliches Sprachlevel
          <span className="funnel-label-hint"> — nur relevant bei ausländischen Fachkräften</span>
        </label>
        <div className="pill-group">
          {SPRACHLEVEL.map((s) => (
            <button key={s.value} type="button" className={`pill-btn ${data.sprachlevel === s.value ? 'pill-btn--selected' : ''}`} onClick={() => updateField('sprachlevel', s.value)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

function QBedarfArbeitszeit({ data, updateField }: QProps) {
  const toggleM = (v: string) => {
    const cur = data.arbeitsmodelle || []
    updateField('arbeitsmodelle', cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])
  }
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label">Arbeitszeit-Modelle <span className="funnel-label-hint">— Mehrfachauswahl</span></label>
        <div className="chip-group">
          {ARBEITSMODELLE.map((m) => (
            <button key={m.value} type="button" className={`chip-btn ${(data.arbeitsmodelle || []).includes(m.value) ? 'chip-btn--selected' : ''}`} onClick={() => toggleM(m.value)}>
              {m.icon} {m.label}<span className="chip-check">✓</span>
            </button>
          ))}
        </div>
      </div>
      <div className="funnel-input-row">
        <NumberField label="Wochenstunden von" value={data.wochenstunden_min} onChange={(v) => updateField('wochenstunden_min', v)} placeholder="z.B. 20" />
        <NumberField label="Wochenstunden bis" value={data.wochenstunden_max} onChange={(v) => updateField('wochenstunden_max', v)} placeholder="z.B. 40" />
      </div>
    </>
  )
}

/* ============================================
   PHASE 3 — VERGÜTUNG & BENEFITS
   ============================================ */

function QVerguetung({ data, updateField }: QProps) {
  const toggleBenefit = (v: string) => {
    const cur = data.benefits || []
    updateField('benefits', cur.includes(v) ? cur.filter((b) => b !== v) : [...cur, v])
  }
  return (
    <>
      <div className="funnel-input-row">
        <NumberField label="Gehalt von (brutto/Monat)" value={data.gehalt_min} onChange={(v) => updateField('gehalt_min', v)} placeholder="z.B. 3200" />
        <NumberField label="Gehalt bis (brutto/Monat)" value={data.gehalt_max} onChange={(v) => updateField('gehalt_max', v)} placeholder="z.B. 4000" />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Zuschläge</label>
        <div className="pill-group">
          {ZUSCHLAEGE_STUFEN.map((z) => (
            <button
              key={z.value}
              type="button"
              className={`pill-btn ${data.zuschlaege === z.value ? 'pill-btn--selected' : ''}`}
              onClick={() => updateField('zuschlaege', z.value)}
            >
              {z.label}
            </button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Benefits <span className="funnel-label-hint">— Mehrfachauswahl</span></label>
        <div className="chip-group">
          {BENEFITS_OPTIONEN.map((b) => (
            <button key={b.value} type="button" className={`chip-btn ${(data.benefits || []).includes(b.value) ? 'chip-btn--selected' : ''}`} onClick={() => toggleBenefit(b.value)}>
              {b.label}<span className="chip-check">✓</span>
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

/* ============================================
   PHASE 4 — DIENSTPLAN & FAKTEN
   ============================================ */

function QSchichtAnforderung({ data, updateField }: QProps) {
  return (
    <>
      <p className="question-hint">
        Geben Sie an, bei welchen Diensten Sie aktuell besonders Bedarf an Pflegekräften mit
        entsprechender Bereitschaft haben.
      </p>
      <div className="funnel-field">
        <label className="funnel-label">Nachtdienste</label>
        <HaeufigkeitToggle value={data.nachtdienst_erforderlich} onChange={(v) => updateField('nachtdienst_erforderlich', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Wochenenddienste</label>
        <HaeufigkeitToggle value={data.wochenenddienst_erforderlich} onChange={(v) => updateField('wochenenddienst_erforderlich', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Geteilte Dienste</label>
        <HaeufigkeitToggle value={data.geteilte_dienste} onChange={(v) => updateField('geteilte_dienste', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Rufbereitschaft</label>
        <HaeufigkeitToggle value={data.rufbereitschaft} onChange={(v) => updateField('rufbereitschaft', v)} />
      </div>
    </>
  )
}

function QDienstplanFakten({ data, updateField }: QProps) {
  return (
    <>
      <InfoExpand
        summary="Bitte schätzen Sie ehrlich ein — diese Angaben helfen beim Matching."
        details={TRANSPARENZ_INFO}
      />
      <NumberField label="Dienstplan-Vorlauf" hint="Wochen im Voraus" value={data.dienstplan_vorlauf_wochen} onChange={(v) => updateField('dienstplan_vorlauf_wochen', v)} placeholder="z.B. 4" />
      <NumberField label="Durchschnittliche Überstunden" hint="pro Monat & Person" value={data.ueberstunden_pro_monat} onChange={(v) => updateField('ueberstunden_pro_monat', v)} placeholder="z.B. 8" />
      <div className="funnel-field">
        <label className="funnel-label">Wie oft kommt Einspringen aus der Freizeit vor?</label>
        <div className="pill-group">
          {EINSPRINGEN_HAEUFIGKEIT.map((o) => (
            <button key={o.value} type="button" className={`pill-btn ${data.einspringen_haeufigkeit === o.value ? 'pill-btn--selected' : ''}`} onClick={() => updateField('einspringen_haeufigkeit', o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Wochenend-Frequenz</label>
        <div className="pill-group">
          {WOCHENEND_FREQUENZ.map((o) => (
            <button key={o.value} type="button" className={`pill-btn ${data.wochenend_frequenz === o.value ? 'pill-btn--selected' : ''}`} onClick={() => updateField('wochenend_frequenz', o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

function QFakten({ data, updateField }: QProps) {
  return (
    <>
      <InfoExpand
        summary="Konkrete, überprüfbare Rahmenbedingungen Ihrer Einrichtung."
        details={TRANSPARENZ_INFO}
      />
      <div className="funnel-field">
        <label className="funnel-label">Digitale Dokumentation vorhanden?</label>
        <YesNo value={data.digitale_doku} onChange={(v) => updateField('digitale_doku', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Pflegehilfskräfte im Dienst vorhanden?</label>
        <YesNo value={data.hilfskraefte_vorhanden} onChange={(v) => updateField('hilfskraefte_vorhanden', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Hebehilfen / technische Hilfsmittel vorhanden?</label>
        <YesNo value={data.hebehilfen_vorhanden} onChange={(v) => updateField('hebehilfen_vorhanden', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Betreuungsschlüssel <span className="funnel-label-hint">— falls bekannt</span></label>
        <input className="funnel-input" type="text" placeholder="z.B. 1:12 im Frühdienst" value={data.betreuungsschluessel} onChange={(e) => updateField('betreuungsschluessel', e.target.value)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Feedbackgespräche</label>
        <div className="pill-group">
          {FEEDBACK_RHYTHMUS.map((o) => (
            <button key={o.value} type="button" className={`pill-btn ${data.feedback_rhythmus === o.value ? 'pill-btn--selected' : ''}`} onClick={() => updateField('feedback_rhythmus', o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Feste Praxisanleitung / Mentor vorhanden?</label>
        <YesNo value={data.praxisanleitung} onChange={(v) => updateField('praxisanleitung', v)} />
      </div>
    </>
  )
}

/* ============================================
   PHASE 5 — ARBEITSBEDINGUNGEN (Slider)
   ============================================ */

function QCategory({ funnel, categoryId }: { funnel: AGFunnelHook; categoryId: CategoryId }) {
  const { data, setSlider } = funnel
  const category = CATEGORY_BY_ID[categoryId]
  const sliders = agSlidersForCategory(categoryId)

  return (
    <>
      <p className="question-hint">{category.employerIntro}</p>
      <div className="slider-q-list">
        {sliders.map((s) => {
          const value = data.slider_antworten[s.id] ?? 0
          return (
            <div key={s.id} className="slider-q">
              <div className="slider-q-head">
                <label className="slider-q-label">{s.labelEmployer}</label>
                <span className={`slider-q-val ${value !== 0 ? 'slider-q-val--set' : ''}`}>
                  {value > 0 ? '+' : ''}{value}
                </span>
              </div>
              <BipolarSlider
                value={value}
                onChange={(v) => setSlider(s.id, v)}
                minLabel={s.employerMinLabel}
                maxLabel={s.employerMaxLabel}
              />
            </div>
          )
        })}
      </div>
    </>
  )
}

/* ============================================
   PHASE 6 — ONBOARDING & SERVICE
   ============================================ */

function QOnboarding({ data, updateField }: QProps) {
  return (
    <>
      <NumberField label="Dauer der Einarbeitung" hint="Wochen" value={data.onboarding_dauer_wochen} onChange={(v) => updateField('onboarding_dauer_wochen', v)} placeholder="z.B. 6" />
      <div className="funnel-field">
        <label className="funnel-label">Feste Ansprechperson für neue Mitarbeitende?</label>
        <YesNo value={data.feste_ansprechperson} onChange={(v) => updateField('feste_ansprechperson', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Ist eine Hospitation vorab möglich?</label>
        <YesNo value={data.hospitation_moeglich} onChange={(v) => updateField('hospitation_moeglich', v)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Unterstützung bei einem Umzug möglich?</label>
        <input className="funnel-input" type="text" placeholder="z.B. ja, Wohnung wird gestellt / nein" value={data.umzugshilfe} onChange={(e) => updateField('umzugshilfe', e.target.value)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Unterstützung bei der Anerkennung möglich?</label>
        <input className="funnel-input" type="text" placeholder="z.B. ja, wir begleiten den Prozess / nein" value={data.anerkennungshilfe} onChange={(e) => updateField('anerkennungshilfe', e.target.value)} />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Besonderheiten Ihrer Einrichtung <span className="funnel-label-hint">— optional</span></label>
        <textarea className="funnel-textarea" rows={4} maxLength={600} placeholder="Was macht Ihre Einrichtung besonders?" value={data.besonderheiten} onChange={(e) => updateField('besonderheiten', e.target.value)} />
        <div className="char-counter">{data.besonderheiten.length} / 600</div>
      </div>
    </>
  )
}

/* ============================================
   PHASE 7 — ABSCHLUSS
   ============================================ */

function QConsent({ funnel }: { funnel: AGFunnelHook }) {
  const { data, updateField, markSubmitted } = funnel
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit =
    data.einwilligung_datenschutz &&
    data.einwilligung_kontakt &&
    !!data.unternehmensname &&
    !!data.ansprechpartner_name &&
    !!data.ansprechpartner_email

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const agId = crypto.randomUUID()

      const fakten = {
        dienstplan_vorlauf_wochen: data.dienstplan_vorlauf_wochen,
        ueberstunden_pro_monat: data.ueberstunden_pro_monat,
        einspringen_haeufigkeit: data.einspringen_haeufigkeit || null,
        wochenend_frequenz: data.wochenend_frequenz || null,
        digitale_doku: data.digitale_doku,
        hilfskraefte_vorhanden: data.hilfskraefte_vorhanden,
        hebehilfen_vorhanden: data.hebehilfen_vorhanden,
        betreuungsschluessel: data.betreuungsschluessel || null,
        feedback_rhythmus: data.feedback_rhythmus || null,
        praxisanleitung: data.praxisanleitung,
      }

      const { error: agError } = await supabase.from('arbeitgeber').insert({
        id: agId,
        name: data.unternehmensname,
        einrichtungstyp: data.einrichtungstyp || null,
        plz: data.plz || null,
        ort: data.ort || null,
        bundesland: data.bundesland || null,
        herkunft: 'funnel',
        status: 'neu',

        // Bedarf
        wunschqualifikationen: data.gewuenschte_qualifikationen.length ? data.gewuenschte_qualifikationen : null,
        einsatzbereiche: data.einsatzbereiche.length ? data.einsatzbereiche : null,
        arbeitsmodelle: data.arbeitsmodelle.length ? data.arbeitsmodelle : null,
        wochenstunden_min: data.wochenstunden_min,
        wochenstunden_max: data.wochenstunden_max,
        mindestberufserfahrung: data.mindestberufserfahrung,
        anerkennung_erforderlich: data.anerkennung_erforderlich || null,
        sprachlevel: data.sprachlevel || null,

        // Vergütung
        gehaltsrahmen_min: data.gehalt_min,
        gehaltsrahmen_max: data.gehalt_max,
        zuschlaege: data.zuschlaege || null,
        benefits: data.benefits.length ? data.benefits : null,

        // Schicht-Anforderungen
        nachtdienst_erforderlich: data.nachtdienst_erforderlich || 'unbekannt',
        wochenenddienst_erforderlich: data.wochenenddienst_erforderlich || null,
        geteilte_dienste: data.geteilte_dienste || null,
        rufbereitschaft: data.rufbereitschaft || null,

        // Profil
        slider_antworten: data.slider_antworten,
        fakten,

        // Onboarding & Service
        besonderheiten: data.besonderheiten || null,
        onboarding_dauer_wochen: data.onboarding_dauer_wochen,
        feste_ansprechperson: data.feste_ansprechperson,
        hospitation_moeglich: data.hospitation_moeglich,
        umzugshilfe: data.umzugshilfe || null,
        anerkennungshilfe: data.anerkennungshilfe || null,
      })
      if (agError) throw agError

      const { error: apError } = await supabase.from('arbeitgeber_ansprechpartner').insert({
        arbeitgeber_id: agId,
        name: data.ansprechpartner_name,
        position: data.ansprechpartner_position || null,
        email: data.ansprechpartner_email || null,
        telefon: data.ansprechpartner_telefon || null,
        ist_hauptkontakt: true,
      })
      if (apError) throw apError

      markSubmitted()
    } catch (err) {
      console.error('AG submit error:', err)
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
      setSubmitting(false)
    }
  }

  return (
    <>
      <p className="question-hint">Bitte bestätigen Sie die folgenden Einwilligungen.</p>
      <div className="consent-group">
        <div className="consent-item" onClick={() => updateField('einwilligung_datenschutz', !data.einwilligung_datenschutz)}>
          <div className={`consent-checkbox ${data.einwilligung_datenschutz ? 'consent-checkbox--checked' : ''}`}>{data.einwilligung_datenschutz && '✓'}</div>
          <div>
            <span className="consent-label"><strong>Datenschutzerklärung *</strong><br />Ich habe die Datenschutzerklärung gelesen und bin mit der Verarbeitung der Daten einverstanden.</span>
            {!data.einwilligung_datenschutz && <><br /><span className="consent-required">Pflichtfeld</span></>}
          </div>
        </div>
        <div className="consent-item" onClick={() => updateField('einwilligung_kontakt', !data.einwilligung_kontakt)}>
          <div className={`consent-checkbox ${data.einwilligung_kontakt ? 'consent-checkbox--checked' : ''}`}>{data.einwilligung_kontakt && '✓'}</div>
          <div>
            <span className="consent-label"><strong>Kontaktaufnahme *</strong><br />Medi-Lane darf uns zu passenden Pflegekräften kontaktieren.</span>
            {!data.einwilligung_kontakt && <><br /><span className="consent-required">Pflichtfeld</span></>}
          </div>
        </div>
      </div>

      {error && <div className="submit-error animate-shake">⚠️ {error}</div>}

      <div className="funnel-nav" style={{ marginTop: 'var(--space-4)' }}>
        <button className="funnel-btn funnel-btn--secondary" onClick={funnel.prev}>← Zurück</button>
        <button className="funnel-btn funnel-btn--primary funnel-btn--submit" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? '⏳ Wird gesendet...' : '✅ Einrichtungsprofil absenden'}
        </button>
      </div>
    </>
  )
}
